import { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUpRight, ArrowDownRight, ChevronLeft, ChevronRight, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import TradeDetail from "./TradeDetail";
import { parseTime, fmtDateTime, fmtPnl, pnlCls } from "./tradeFormat";

const loadError = (e) => {
  const s = e?.response?.status;
  if (s === 401 || s === 403) return "Your sign-in has expired. Please sign in again to see the trades.";
  if (!e?.response) return "Could not reach the server. Check the connection and try again.";
  return `The server could not load the trades (error ${s}). Try again in a moment.`;
};

export default function TradeHistory() {
  const [trades, setTrades] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ instrument: '', direction: '', result: '' });
  const [page, setPage] = useState(0);
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.instrument) params.set('instrument', filters.instrument);
      if (filters.direction) params.set('direction', filters.direction);
      if (filters.result) params.set('result', filters.result);
      params.set('limit', limit);
      params.set('offset', page * limit);
      const { data } = await api.get(`/ops/history?${params.toString()}`);
      setTrades(data.trades || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (e) {
      setError(loadError(e));
    }
    setLoading(false);
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / limit);

  const exportCSV = async () => {
    try {
      toast.info('Preparing export...');
      const params = new URLSearchParams();
      if (filters.instrument) params.set('instrument', filters.instrument);
      if (filters.direction) params.set('direction', filters.direction);
      if (filters.result) params.set('result', filters.result);
      params.set('limit', '500');
      const rows = [];
      for (let offset = 0; offset < 100000; offset += 500) {
        params.set('offset', String(offset));
        const { data } = await api.get(`/ops/history?${params.toString()}`);
        rows.push(...(data.trades || []));
        if ((data.trades || []).length < 500 || rows.length >= (data.total || 0)) break;
      }
      if (rows.length === 0) { toast.warning('No trades to export'); return; }

      const headers = ['Date','Instrument','Direction','Setup','Score','Entry','Exit','Qty','P&L','Result','Exit Reason'];
      const csvRows = [headers.join(',')];
      rows.forEach(t => {
        const d = parseTime(t.entry_time);
        const date = d ? d.toISOString() : (t.entry_time || '');
        const line = [
          `"${date}"`, t.instrument, t.direction, `"${(t.setup || '').replace(/"/g, '""')}"`,
          t.mentor_score ?? '', t.entry_price?.toFixed(2) ?? '', t.exit_price?.toFixed(2) ?? '',
          t.quantity ?? '', t.pnl?.toFixed(2) ?? '', t.result, `"${(t.exit_reason || '').replace(/"/g, '""')}"`
        ];
        csvRows.push(line.join(','));
      });

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mentorai_trades_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} trades`);
    } catch (e) {
      toast.error('Export failed');
    }
  };

  return (
    <div data-testid="ops-history-page" className="space-y-5">
      <div>
        <h1 className="text-lg font-display font-semibold text-[hsl(var(--ops-text))]">Trade History</h1>
        <p className="text-xs text-[hsl(var(--ops-text-muted))]">Complete log of all executed trades. Tap a trade to see the chart and why the bot took it.</p>
      </div>

      {/* Filters */}
      <Card className="p-3 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))]">
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={filters.instrument} onValueChange={(v) => { setFilters({...filters, instrument: v === 'all' ? '' : v}); setPage(0); }}>
            <SelectTrigger data-testid="ops-history-filter-instrument" className="w-[130px] h-8 text-xs bg-[hsl(var(--ops-surface-2))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))]">
              <SelectValue placeholder="Instrument" />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))]">
              <SelectItem value="all">All Instruments</SelectItem>
              <SelectItem value="MNQ">MNQ</SelectItem>
              <SelectItem value="MES">MES</SelectItem>
              <SelectItem value="MCL">MCL</SelectItem>
              <SelectItem value="MGC">MGC</SelectItem>
              <SelectItem value="M2K">M2K</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.direction} onValueChange={(v) => { setFilters({...filters, direction: v === 'all' ? '' : v}); setPage(0); }}>
            <SelectTrigger data-testid="ops-history-filter-direction" className="w-[110px] h-8 text-xs bg-[hsl(var(--ops-surface-2))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))]">
              <SelectValue placeholder="Direction" />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))]">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="LONG">LONG</SelectItem>
              <SelectItem value="SHORT">SHORT</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.result} onValueChange={(v) => { setFilters({...filters, result: v === 'all' ? '' : v}); setPage(0); }}>
            <SelectTrigger data-testid="ops-history-filter-result" className="w-[100px] h-8 text-xs bg-[hsl(var(--ops-surface-2))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))]">
              <SelectValue placeholder="Result" />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))]">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="WIN">WIN</SelectItem>
              <SelectItem value="LOSS">LOSS</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-[hsl(var(--ops-text-muted))]">{total} trades</span>
            <Button data-testid="ops-history-export-csv" variant="ghost" size="sm" className="h-7 text-[11px] px-2 gap-1 text-[hsl(var(--ops-text-muted))] hover:text-[hsl(var(--ops-text))]" onClick={exportCSV} disabled={loading || total === 0}>
              <Download className="size-3" /> Export CSV
            </Button>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-2">{Array.from({length:10}).map((_,i)=><Skeleton key={i} className="h-8 bg-[hsl(var(--ops-surface-2))]" />)}</div>
        ) : error ? (
          <div data-testid="ops-history-error" className="px-3 py-12 text-center space-y-2">
            <p className="text-sm text-[hsl(var(--ops-text-muted))]">{error}</p>
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={load}><RefreshCw className="size-3" /> Try again</Button>
          </div>
        ) : trades.length === 0 ? (
          <div className="px-3 py-12 text-center text-sm text-[hsl(var(--ops-text-muted))]">No trades match your filters</div>
        ) : (
          <>
          {/* Phones: stacked cards */}
          <div data-testid="ops-history-cards" className="sm:hidden divide-y divide-[hsl(var(--ops-border))]">
            {trades.map((t, i) => (
              <button key={t.trade_id || i} type="button" disabled={!t.trade_id} onClick={() => setSelected(t)}
                data-testid="ops-history-card"
                className="w-full text-left px-3 py-2.5 flex items-center gap-3 active:bg-[hsl(var(--ops-surface-2))] disabled:cursor-default">
                <div className="min-w-0 flex-1 space-y-0.5">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge className={`rounded text-[10px] border-0 px-1 gap-0.5 ${t.direction === 'LONG' ? 'bg-[hsl(var(--ops-long))]/15 text-[hsl(var(--ops-long))]' : 'bg-[hsl(var(--ops-short))]/15 text-[hsl(var(--ops-short))]'}`}>
                      {t.direction === 'LONG' ? <ArrowUpRight className="size-2.5" /> : <ArrowDownRight className="size-2.5" />}
                      {t.direction}
                    </Badge>
                    <span className="text-xs font-medium text-[hsl(var(--ops-text))]">{t.instrument}</span>
                    {t.quantity != null && <span className="text-[11px] text-[hsl(var(--ops-text-muted))]">× {t.quantity}</span>}
                    {t.result && (
                      <Badge className={`rounded text-[10px] border-0 px-1.5 ${t.result === 'WIN' ? 'bg-[hsl(var(--ops-long))]/15 text-[hsl(var(--ops-long))]' : 'bg-[hsl(var(--ops-short))]/15 text-[hsl(var(--ops-short))]'}`}>{t.result}</Badge>
                    )}
                  </div>
                  <div className="text-[11px] font-mono-num text-[hsl(var(--ops-text-muted))] break-words">
                    {fmtDateTime(t.entry_time)} · {t.entry_price?.toFixed(2) ?? '—'} → {t.exit_price != null ? t.exit_price.toFixed(2) : 'open'}
                    {t.exit_reason ? ` · ${t.exit_reason}` : ''}
                  </div>
                </div>
                <span className={`text-sm font-mono-num font-semibold shrink-0 ${pnlCls(t.pnl)}`}>{t.pnl != null ? fmtPnl(t.pnl) : '—'}</span>
                {t.trade_id && <ChevronRight className="size-4 shrink-0 text-[hsl(var(--ops-text-muted))]" />}
              </button>
            ))}
          </div>
          <div className="hidden sm:block overflow-x-auto">
            <table data-testid="ops-history-table" className="w-full text-xs">
              <thead>
                <tr className="border-b border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text-muted))]">
                  <th className="text-left py-2 px-3 font-medium">Date / Time</th>
                  <th className="text-left py-2 px-3 font-medium">Instr</th>
                  <th className="text-center py-2 px-3 font-medium">Dir</th>
                  <th className="text-left py-2 px-3 font-medium">Setup</th>
                  <th className="text-right py-2 px-3 font-medium">Score</th>
                  <th className="text-right py-2 px-3 font-medium">Entry</th>
                  <th className="text-right py-2 px-3 font-medium">Exit</th>
                  <th className="text-right py-2 px-3 font-medium">Qty</th>
                  <th className="text-right py-2 px-3 font-medium">P&L</th>
                  <th className="text-center py-2 px-3 font-medium">Result</th>
                  <th className="text-left py-2 px-3 font-medium">Exit Reason</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t, i) => (
                  <tr key={i} onClick={t.trade_id ? () => setSelected(t) : undefined}
                    tabIndex={t.trade_id ? 0 : undefined}
                    onKeyDown={t.trade_id ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected(t); } } : undefined}
                    className={`border-b border-[hsl(var(--ops-border))]/50 hover:bg-[hsl(var(--ops-surface-2))] transition-colors duration-100 h-8 ${t.trade_id ? 'cursor-pointer focus-visible:outline-none focus-visible:bg-[hsl(var(--ops-surface-2))]' : ''}`}>
                    <td className="py-1 px-3 font-mono-num text-[hsl(var(--ops-text-muted))]">{fmtDateTime(t.entry_time)}</td>
                    <td className="py-1 px-3 font-medium text-[hsl(var(--ops-text))]">{t.instrument}</td>
                    <td className="py-1 px-3 text-center">
                      <Badge className={`rounded text-[10px] border-0 px-1 gap-0.5 ${t.direction === 'LONG' ? 'bg-[hsl(var(--ops-long))]/15 text-[hsl(var(--ops-long))]' : 'bg-[hsl(var(--ops-short))]/15 text-[hsl(var(--ops-short))]'}`}>
                        {t.direction === 'LONG' ? <ArrowUpRight className="size-2.5" /> : <ArrowDownRight className="size-2.5" />}
                        {t.direction}
                      </Badge>
                    </td>
                    <td className="py-1 px-3 text-[hsl(var(--ops-text-muted))] max-w-[120px] truncate">{t.setup}</td>
                    <td className="py-1 px-3 text-right font-mono-num text-[hsl(var(--ops-brand))]">{t.mentor_score}</td>
                    <td className="py-1 px-3 text-right font-mono-num">{t.entry_price?.toFixed(2)}</td>
                    <td className="py-1 px-3 text-right font-mono-num">{t.exit_price?.toFixed(2)}</td>
                    <td className="py-1 px-3 text-right font-mono-num">{t.quantity}</td>
                    <td className={`py-1 px-3 text-right font-mono-num font-semibold ${pnlCls(t.pnl)}`}>{fmtPnl(t.pnl)}</td>
                    <td className="py-1 px-3 text-center">
                      <Badge className={`rounded text-[10px] border-0 px-1.5 ${t.result === 'WIN' ? 'bg-[hsl(var(--ops-long))]/15 text-[hsl(var(--ops-long))]' : 'bg-[hsl(var(--ops-short))]/15 text-[hsl(var(--ops-short))]'}`}>{t.result}</Badge>
                    </td>
                    <td className="py-1 px-3 text-[hsl(var(--ops-text-muted))] max-w-[140px] truncate">{t.exit_reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-3 py-2 border-t border-[hsl(var(--ops-border))] flex items-center justify-between">
            <span className="text-xs text-[hsl(var(--ops-text-muted))]">Page {page + 1} of {totalPages}</span>
            <div className="flex gap-1">
              <Button data-testid="ops-history-prev" variant="ghost" size="icon" className="size-7" disabled={page === 0} onClick={() => setPage(p => p - 1)}><ChevronLeft className="size-4" /></Button>
              <Button data-testid="ops-history-next" variant="ghost" size="icon" className="size-7" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}><ChevronRight className="size-4" /></Button>
            </div>
          </div>
        )}
      </Card>

      {selected && <TradeDetail trade={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
